import { MultipleOptionsResponseDto } from "@se/platform/forms/dtos/out/responses/multipleOptionsResponseDto";
import { fromEvent, Subject } from "rxjs";
import { pluck } from "rxjs/operators";
import { IMessagingService } from "../../services";
import { Form } from "../form";
import { FormFieldComponent } from "../formFieldComponent";

class CheckboxDropdownComponent extends FormFieldComponent {
    private wrapperEl: HTMLElement;
    private buttonEl: HTMLButtonElement;
    private optionEls: HTMLLIElement[];
    private inputEls: HTMLInputElement[];
    private focusedOptionIndex$ = new Subject<number>();
    private focusedOptionIndex: number = -1;
    private onDocKeydownBound: (e: KeyboardEvent) => void;
    private isExpanded: boolean = false;
    private isFocused: boolean = false;

    constructor(protected fieldEl: HTMLElement, messagingService: IMessagingService, protected form: Form) {
        super(fieldEl, messagingService, form);


        this.wrapperEl = fieldEl.querySelector(".js-form-field-dropdown-wrapper");
        this.buttonEl = fieldEl.querySelector(".js-form-field-dropdown-button");
        this.optionEls = Array.from(fieldEl.querySelectorAll(".js-form-field-dropdown-option"));
        this.inputEls = Array.from(fieldEl.querySelectorAll<HTMLInputElement>(".js-form-field-dropdown-option-checkbox"));

        this.onDocKeydownBound = this.onDocKeydown.bind(this);

        this.buttonEl.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.isExpanded === false) {
                this.expand();
                this.buttonEl.focus();
            } else if (this.isExpanded === true) {
                this.collapse();
            }
        });

        this.buttonEl.addEventListener("focus", () => {
            if (this.isFocused === false) {
                document.addEventListener("keydown", this.onDocKeydownBound, true);
                this.isFocused = true;
            }
        });

        this.wrapperEl.addEventListener("focusout", this.onFocusout.bind(this));

        this.inputEls.forEach((checkboxEl) => {
            // listen for any of the checkbox fields changing, and extract its value
            const onCheckboxClicked$ = fromEvent(checkboxEl, "change").pipe(pluck("target", "value"));

            // when the form value changes update the value in the store
            onCheckboxClicked$.subscribe((value: string) => {
                const selectedOptions = this.inputEls.filter((a) => a.checked).map((checkbox) => checkbox.value);
                super.setResponseValue(selectedOptions);
            });
        });

        this.focusedOptionIndex$.subscribe((index) => {
            this.focusedOptionIndex = index;
            this.hoverOption(index);
        });
    }

    // update the value of this field in the DOM.
    protected setDisplayValue(value: string[]) {
        
        if(value) {
            this.inputEls.forEach((el) => {
                el.checked = value.indexOf(el.value) > -1;
            });
        
            switch (value.length) {
                case 0:
                    this.buttonEl.textContent = "No options selected";
                    break;
                case 1:
                    this.buttonEl.textContent = "1 option selected";
                    break;
                default:
                    this.buttonEl.textContent = `${value.length} options selected`;
                    break;
            }
        }
    }
    
    protected areEqual(prev: MultipleOptionsResponseDto, next: MultipleOptionsResponseDto) {
        if (next.value.length != prev.value.length) {
            return false;
        }

        const sortedPrevValues = prev.value.sort();
        const sortedNextValues = next.value.sort();

        // loop over our sorted prev/next values and compare each item.
        for (let i = 0; i < sortedPrevValues.length; i++) {
            if (sortedPrevValues[i] != sortedNextValues[i]) {
                return false;
            }
        }

        return true;
    }

    onFocusout(e: FocusEvent) {
        if (e.relatedTarget && this.wrapperEl.contains(e.relatedTarget as HTMLElement)) return;
        document.removeEventListener("keydown", this.onDocKeydownBound, true);
        this.focusedOptionIndex = -1;
        this.collapse();
        this.isFocused = false;
    }

    selectOption(index: number) {
        if (index > -1) {
            this.inputEls[index].checked = !this.inputEls[index].checked;
            this.optionEls[index].setAttribute("aria-selected", this.inputEls[index].checked.toString());
            const changeEvent = new Event("change");
            this.inputEls[index].dispatchEvent(changeEvent);
        }
    }

    expand() {
        this.containerEl.classList.add("form-field--dropdown--expanded");
        this.buttonEl.setAttribute("aria-expanded", "true");
        this.isExpanded = true;
    }

    collapse() {
        this.containerEl.classList.remove("form-field--dropdown--expanded");
        this.buttonEl.setAttribute("aria-expanded", "false");
        this.isExpanded = false;
    }

    hoverOption(index: number) {
        this.optionEls.forEach((el, i) => {
            if (index === i) {
                el.setAttribute("aria-selected", "true");
                el.focus();
            } else {
                el.setAttribute("aria-selected", "false");
            }
        });
    }

    onDocKeydown(e: KeyboardEvent) {
        switch (e.code) {
            case "ArrowDown": {
                this.onArrowDown(e);
                break;
            }
            case "ArrowUp": {
                this.onArrowUp(e);
                break;
            }
            case "Escape": {
                this.onEscape(e);
                break;
            }
            case "Enter": {
                this.onEnter(e);
            }
            default:
                return;
        }
    }

    onArrowDown(e: KeyboardEvent) {
        e.preventDefault();
        if (this.focusedOptionIndex === -1) {
            this.expand();
        }
        if (this.focusedOptionIndex < this.optionEls.length - 1) {
            this.focusedOptionIndex$.next(this.focusedOptionIndex + 1);
        }
    }

    onArrowUp(e: KeyboardEvent) {
        e.preventDefault();
        if (this.focusedOptionIndex === 0) {
            this.focusedOptionIndex$.next(-1);
            this.collapse();
            this.buttonEl.focus();
        }
        if (this.focusedOptionIndex > 0) {
            this.focusedOptionIndex$.next(this.focusedOptionIndex - 1);
        }
    }

    onEscape(e: KeyboardEvent) {
        this.focusedOptionIndex$.next(this.focusedOptionIndex - 1);
        this.collapse();
        this.buttonEl.focus();
    }

    onEnter(e: KeyboardEvent) {
        e.preventDefault();
        if (this.focusedOptionIndex === -1 && this.isExpanded === false) {
            this.expand();
        } else if (this.focusedOptionIndex === -1 && this.isExpanded === true) {
            this.collapse();
        } else if (this.focusedOptionIndex > -1 && this.focusedOptionIndex < this.optionEls.length) {
            this.selectOption(this.focusedOptionIndex);
        }
    }

    public setFocus() {
        this.buttonEl.focus();
    }
}

export { CheckboxDropdownComponent };
